import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save,
  Settings,
  DollarSign,
  FileText,
  Bell,
  Shield,
  Palette,
  CreditCard,
  Upload,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Edit2
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const InvoiceSettingsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // 1️⃣ General Settings
  const [generalSettings, setGeneralSettings] = useState({
    currency: 'SAR',
    taxRate: 15,
    numberingMethod: 'auto',
    prefix: 'INV',
    startNumber: 1001,
    defaultDueDays: 30
  });

  // 2️⃣ Invoice Customization
  const [customization, setCustomization] = useState({
    logo: null,
    footer: 'شكراً لتعاملكم معنا - نتطلع لخدمتكم دائماً',
    signature: null,
    companyName: 'مكتب وزين للمحاماة والاستشارات القانونية',
    companyAddress: 'الرياض، المملكة العربية السعودية',
    companyPhone: '+966 11 234 5678',
    companyEmail: 'info@wazin.law',
    paymentTerms: 'يرجى السداد خلال المدة المحددة'
  });

  // 3️⃣ Status Control
  const [statuses, setStatuses] = useState([
    { id: 1, name: 'مسودة', nameEn: 'draft', color: '#6B7280', canChange: ['admin', 'finance_manager'] },
    { id: 2, name: 'معلقة', nameEn: 'pending', color: '#F59E0B', canChange: ['admin', 'finance_manager'] },
    { id: 3, name: 'مدفوعة', nameEn: 'paid', color: '#10B981', canChange: ['admin', 'finance_manager', 'accountant'] },
    { id: 4, name: 'متأخرة', nameEn: 'overdue', color: '#EF4444', canChange: ['admin', 'finance_manager'] },
    { id: 5, name: 'ملغاة', nameEn: 'cancelled', color: '#6B7280', canChange: ['admin'] }
  ]);

  // 4️⃣ Payment Options
  const [paymentMethods, setPaymentMethods] = useState([
    { id: 1, name: 'نقداً', nameEn: 'cash', enabled: true, gateway: null },
    { id: 2, name: 'بطاقة ائتمانية', nameEn: 'credit_card', enabled: true, gateway: null },
    { id: 3, name: 'تحويل بنكي', nameEn: 'bank_transfer', enabled: true, gateway: null },
    { id: 4, name: 'شبكة', nameEn: 'mada', enabled: true, gateway: null }
  ]);

  // 5️⃣ Notifications
  const [notifications, setNotifications] = useState({
    reminderBeforeDue: true,
    reminderDaysBefore: 3,
    alertAfterDue: true,
    alertDaysAfter: 1,
    emailTemplate: `عزيزي العميل،\n\nنود تذكيركم بأن الفاتورة رقم {invoice_number} بمبلغ {amount} ر.س\nمستحقة بتاريخ {due_date}\n\nنرجو منكم التكرم بالسداد في الموعد المحدد.\n\nمع خالص التقدير،\nفريق وزين`
  });

  // 6️⃣ Permissions
  const [permissions, setPermissions] = useState({
    create: ['admin', 'finance_manager', 'accountant'],
    approve: ['admin', 'finance_manager'],
    cancel: ['admin'],
    edit: ['admin', 'finance_manager']
  });

  const tabs = [
    { id: 'general', label: 'إعدادات عامة', icon: Settings },
    { id: 'customization', label: 'تخصيص الفاتورة', icon: Palette },
    { id: 'statuses', label: 'الحالات', icon: FileText },
    { id: 'payment', label: 'خيارات الدفع', icon: CreditCard },
    { id: 'notifications', label: 'الإشعارات', icon: Bell },
    { id: 'permissions', label: 'الصلاحيات', icon: Shield }
  ];

  const handleSave = async () => {
    setLoading(true);
    setSaveSuccess(false);

    // Mock API call
    setTimeout(() => {
      console.log('Saving settings:', {
        generalSettings,
        customization,
        statuses,
        paymentMethods,
        notifications,
        permissions
      });
      setLoading(false);
      setSaveSuccess(true);
      
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }, 1000);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Mock file upload
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomization(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomization(prev => ({ ...prev, signature: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const togglePaymentMethod = (id) => {
    setPaymentMethods(prev =>
      prev.map(method =>
        method.id === id ? { ...method, enabled: !method.enabled } : method
      )
    );
  };

  const addStatus = () => {
    const newStatus = {
      id: statuses.length + 1,
      name: 'حالة جديدة',
      nameEn: 'new_status',
      color: '#6B7280',
      canChange: ['admin']
    };
    setStatuses([...statuses, newStatus]);
  };

  const deleteStatus = (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الحالة؟')) {
      setStatuses(statuses.filter(s => s.id !== id));
    }
  };

  if (!hasPermission('finance.settings')) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">ليس لديك صلاحية الوصول لهذه الصفحة</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <Settings className="w-8 h-8" />
                <span>إعدادات الفواتير</span>
              </h1>
              <p className="text-gray-600">إدارة وتخصيص إعدادات نظام الفواتير</p>
            </div>

            <button
              onClick={handleSave}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>جاري الحفظ...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>حفظ الإعدادات</span>
                </>
              )}
            </button>
          </div>

          {/* Success Message */}
          {saveSuccess && (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-green-700 font-semibold">تم حفظ الإعدادات بنجاح!</p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 mb-6">
          <div className="flex overflow-x-auto border-b-2 border-gray-200">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-bold transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-[#1E3A8A] text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {/* 1️⃣ General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h2 className="text-xl font-black text-gray-800 flex items-center gap-2 mb-4">
                  <DollarSign className="w-6 h-6 text-[#1E3A8A]" />
                  الإعدادات العامة
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      العملة الافتراضية
                    </label>
                    <select
                      value={generalSettings.currency}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, currency: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                    >
                      <option value="SAR">ريال سعودي (SAR)</option>
                      <option value="USD">دولار أمريكي (USD)</option>
                      <option value="EUR">يورو (EUR)</option>
                    </select>
                  </div>

                  {/* Tax Rate */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      نسبة الضريبة الافتراضية (%)
                    </label>
                    <input
                      type="number"
                      value={generalSettings.taxRate}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, taxRate: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>

                  {/* Numbering Method */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      طريقة الترقيم
                    </label>
                    <select
                      value={generalSettings.numberingMethod}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, numberingMethod: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                    >
                      <option value="auto">ترقيم تلقائي</option>
                      <option value="manual">ترقيم يدوي</option>
                    </select>
                  </div>

                  {/* Prefix */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      البادئة (Prefix)
                    </label>
                    <input
                      type="text"
                      value={generalSettings.prefix}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, prefix: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      placeholder="INV"
                    />
                  </div>

                  {/* Start Number */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      رقم البداية
                    </label>
                    <input
                      type="number"
                      value={generalSettings.startNumber}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, startNumber: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      min="1"
                    />
                  </div>

                  {/* Default Due Days */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      مدة الاستحقاق الافتراضية (أيام)
                    </label>
                    <input
                      type="number"
                      value={generalSettings.defaultDueDays}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, defaultDueDays: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      min="0"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-bold mb-1">ملاحظة:</p>
                    <p>ستطبق هذه الإعدادات على جميع الفواتير الجديدة. الفواتير الحالية لن تتأثر.</p>
                  </div>
                </div>
              </div>
            )}

            {/* 2️⃣ Invoice Customization */}
            {activeTab === 'customization' && (
              <div className="space-y-6">
                <h2 className="text-xl font-black text-gray-800 flex items-center gap-2 mb-4">
                  <Palette className="w-6 h-6 text-[#1E3A8A]" />
                  تخصيص شكل الفاتورة
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo Upload */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      شعار المنشأة
                    </label>
                    <div className="flex items-center gap-4">
                      {customization.logo && (
                        <img src={customization.logo} alt="Logo" className="h-16 w-auto object-contain border-2 border-gray-200 rounded-lg p-2" />
                      )}
                      <label className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 cursor-pointer transition-all">
                        <Upload className="w-4 h-4" />
                        <span>رفع الشعار</span>
                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                      </label>
                    </div>
                  </div>

                  {/* Company Info */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      اسم المنشأة
                    </label>
                    <input
                      type="text"
                      value={customization.companyName}
                      onChange={(e) => setCustomization({ ...customization, companyName: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      عنوان المنشأة
                    </label>
                    <input
                      type="text"
                      value={customization.companyAddress}
                      onChange={(e) => setCustomization({ ...customization, companyAddress: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      رقم الهاتف
                    </label>
                    <input
                      type="tel"
                      value={customization.companyPhone}
                      onChange={(e) => setCustomization({ ...customization, companyPhone: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      value={customization.companyEmail}
                      onChange={(e) => setCustomization({ ...customization, companyEmail: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      dir="ltr"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      شروط السداد
                    </label>
                    <textarea
                      value={customization.paymentTerms}
                      onChange={(e) => setCustomization({ ...customization, paymentTerms: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      rows="3"
                    ></textarea>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      التذييل
                    </label>
                    <textarea
                      value={customization.footer}
                      onChange={(e) => setCustomization({ ...customization, footer: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      rows="2"
                    ></textarea>
                  </div>

                  {/* Signature Upload */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      التوقيع
                    </label>
                    <div className="flex items-center gap-4">
                      {customization.signature && (
                        <img src={customization.signature} alt="Signature" className="h-16 w-auto object-contain border-2 border-gray-200 rounded-lg p-2" />
                      )}
                      <label className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 cursor-pointer transition-all">
                        <Upload className="w-4 h-4" />
                        <span>رفع التوقيع</span>
                        <input type="file" accept="image/*" onChange={handleSignatureUpload} className="hidden" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 3️⃣ Status Control */}
            {activeTab === 'statuses' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-black text-gray-800 flex items-center gap-2">
                    <FileText className="w-6 h-6 text-[#1E3A8A]" />
                    إدارة حالات الفواتير
                  </h2>
                  <button
                    onClick={addStatus}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] text-white rounded-lg font-bold hover:bg-[#1E40AF] transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    <span>إضافة حالة</span>
                  </button>
                </div>

                <div className="space-y-3">
                  {statuses.map((status) => (
                    <div key={status.id} className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-6 h-6 rounded-full border-2"
                            style={{ backgroundColor: status.color, borderColor: status.color }}
                          ></div>
                          <div>
                            <p className="font-bold text-gray-800">{status.name}</p>
                            <p className="text-xs text-gray-500">{status.nameEn}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => alert('تعديل الحالة')}
                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => deleteStatus(status.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold">من يملك صلاحية التعديل:</span>{' '}
                        {status.canChange.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 4️⃣ Payment Options */}
            {activeTab === 'payment' && (
              <div className="space-y-6">
                <h2 className="text-xl font-black text-gray-800 flex items-center gap-2 mb-4">
                  <CreditCard className="w-6 h-6 text-[#1E3A8A]" />
                  طرق الدفع المتاحة
                </h2>

                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={method.enabled}
                            onChange={() => togglePaymentMethod(method.id)}
                            className="w-5 h-5 text-[#1E3A8A] rounded focus:ring-2 focus:ring-[#1E3A8A]"
                          />
                          <div>
                            <p className="font-bold text-gray-800">{method.name}</p>
                            <p className="text-xs text-gray-500">{method.nameEn}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {method.enabled ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-bold border border-green-200">
                              <CheckCircle className="w-3 h-3" />
                              مفعّل
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-bold border border-gray-300">
                              معطّل
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-700">
                    <p className="font-bold mb-1">ملاحظة:</p>
                    <p>ربط بوابات الدفع الإلكترونية سيكون متاحاً في إصدار لاحق.</p>
                  </div>
                </div>
              </div>
            )}

            {/* 5️⃣ Notifications */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h2 className="text-xl font-black text-gray-800 flex items-center gap-2 mb-4">
                  <Bell className="w-6 h-6 text-[#1E3A8A]" />
                  إعدادات الإشعارات
                </h2>

                <div className="space-y-6">
                  {/* Reminder Before Due */}
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <label className="font-bold text-gray-800">تذكير قبل الاستحقاق</label>
                      <input
                        type="checkbox"
                        checked={notifications.reminderBeforeDue}
                        onChange={(e) => setNotifications({ ...notifications, reminderBeforeDue: e.target.checked })}
                        className="w-5 h-5 text-[#1E3A8A] rounded focus:ring-2 focus:ring-[#1E3A8A]"
                      />
                    </div>
                    {notifications.reminderBeforeDue && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          عدد الأيام قبل الاستحقاق
                        </label>
                        <input
                          type="number"
                          value={notifications.reminderDaysBefore}
                          onChange={(e) => setNotifications({ ...notifications, reminderDaysBefore: parseInt(e.target.value) })}
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                          min="1"
                        />
                      </div>
                    )}
                  </div>

                  {/* Alert After Due */}
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <label className="font-bold text-gray-800">تنبيه بعد التأخير</label>
                      <input
                        type="checkbox"
                        checked={notifications.alertAfterDue}
                        onChange={(e) => setNotifications({ ...notifications, alertAfterDue: e.target.checked })}
                        className="w-5 h-5 text-[#1E3A8A] rounded focus:ring-2 focus:ring-[#1E3A8A]"
                      />
                    </div>
                    {notifications.alertAfterDue && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          عدد الأيام بعد الاستحقاق
                        </label>
                        <input
                          type="number"
                          value={notifications.alertDaysAfter}
                          onChange={(e) => setNotifications({ ...notifications, alertDaysAfter: parseInt(e.target.value) })}
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                          min="1"
                        />
                      </div>
                    )}
                  </div>

                  {/* Email Template */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      قالب رسالة البريد الإلكتروني
                    </label>
                    <textarea
                      value={notifications.emailTemplate}
                      onChange={(e) => setNotifications({ ...notifications, emailTemplate: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-mono text-sm"
                      rows="8"
                    ></textarea>
                    <p className="mt-2 text-xs text-gray-500">
                      يمكنك استخدام المتغيرات التالية: {'{invoice_number}'}, {'{amount}'}, {'{due_date}'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 6️⃣ Permissions */}
            {activeTab === 'permissions' && (
              <div className="space-y-6">
                <h2 className="text-xl font-black text-gray-800 flex items-center gap-2 mb-4">
                  <Shield className="w-6 h-6 text-[#1E3A8A]" />
                  صلاحيات الفواتير
                </h2>

                <div className="space-y-4">
                  {/* Create Permission */}
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      من يمكنه إنشاء فاتورة؟
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['admin', 'finance_manager', 'accountant', 'employee'].map((role) => (
                        <label key={role} className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg cursor-pointer hover:border-[#1E3A8A] transition-colors">
                          <input
                            type="checkbox"
                            checked={permissions.create.includes(role)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPermissions({ ...permissions, create: [...permissions.create, role] });
                              } else {
                                setPermissions({ ...permissions, create: permissions.create.filter(r => r !== role) });
                              }
                            }}
                            className="w-4 h-4 text-[#1E3A8A] rounded focus:ring-2 focus:ring-[#1E3A8A]"
                          />
                          <span className="text-sm font-semibold text-gray-700">{role}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Approve Permission */}
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      من يمكنه اعتماد فاتورة؟
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['admin', 'finance_manager', 'accountant'].map((role) => (
                        <label key={role} className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg cursor-pointer hover:border-[#1E3A8A] transition-colors">
                          <input
                            type="checkbox"
                            checked={permissions.approve.includes(role)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPermissions({ ...permissions, approve: [...permissions.approve, role] });
                              } else {
                                setPermissions({ ...permissions, approve: permissions.approve.filter(r => r !== role) });
                              }
                            }}
                            className="w-4 h-4 text-[#1E3A8A] rounded focus:ring-2 focus:ring-[#1E3A8A]"
                          />
                          <span className="text-sm font-semibold text-gray-700">{role}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Cancel Permission */}
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      من يمكنه إلغاء فاتورة؟
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['admin', 'finance_manager'].map((role) => (
                        <label key={role} className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg cursor-pointer hover:border-[#1E3A8A] transition-colors">
                          <input
                            type="checkbox"
                            checked={permissions.cancel.includes(role)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPermissions({ ...permissions, cancel: [...permissions.cancel, role] });
                              } else {
                                setPermissions({ ...permissions, cancel: permissions.cancel.filter(r => r !== role) });
                              }
                            }}
                            className="w-4 h-4 text-[#1E3A8A] rounded focus:ring-2 focus:ring-[#1E3A8A]"
                          />
                          <span className="text-sm font-semibold text-gray-700">{role}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Edit Permission */}
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      من يمكنه تعديل فاتورة؟
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['admin', 'finance_manager', 'accountant'].map((role) => (
                        <label key={role} className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg cursor-pointer hover:border-[#1E3A8A] transition-colors">
                          <input
                            type="checkbox"
                            checked={permissions.edit.includes(role)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPermissions({ ...permissions, edit: [...permissions.edit, role] });
                              } else {
                                setPermissions({ ...permissions, edit: permissions.edit.filter(r => r !== role) });
                              }
                            }}
                            className="w-4 h-4 text-[#1E3A8A] rounded focus:ring-2 focus:ring-[#1E3A8A]"
                          />
                          <span className="text-sm font-semibold text-gray-700">{role}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-bold mb-1">ملاحظة:</p>
                    <p>الصلاحيات مرتبطة بالأدوار المحددة في نظام إدارة المستخدمين.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default InvoiceSettingsPage;
